// Ficha 360 del cliente (D95): en la ficha de la empresa, una franja con lo de soporte (casos abiertos y del año,
// horas del año, la bolsa con su plan, el ultimo caso) y "Exportar a Excel" con casos, horas, llamadas y consumos.
import { useQuery } from '@tanstack/react-query';

import { HORARIOS, nombrePlan } from '../api/bolsas';
import { casosDe } from '../api/casos';
import { horasEntre, horasTexto } from '../api/proyectos';
import type { Empresa, Llamada } from '../api/types';
import { useSession } from '../auth/session';
import { ago } from '../lib/format';
import { BotonExcel } from '../ui/BotonExcel';
import { useProyectos } from '../ui/HorasForm';
import { bolsaDeEmpresa, PillSaldo, useBolsas } from './ventas/Bolsas';
import { ESTADO, prioridadTexto, usePersona } from './reportes/soporte/comun';

const h = (m: number) => Math.round((m / 60) * 100) / 100;

export function Soporte360({ empresa, llamadas }: { empresa: Empresa; llamadas: Llamada[] }) {
  const exporta = useSession().puede('clientes.exportar');
  const inicioAnio = new Date(new Date().getFullYear(), 0, 1);
  const casos = useQuery({ queryKey: ['casos', 'empresa', empresa.id], queryFn: () => casosDe('empresaId', empresa.id), staleTime: 60_000 });
  const pr = useProyectos();
  const horas = useQuery({ queryKey: ['horas', 'anio'], queryFn: () => horasEntre(inicioAnio, new Date()), staleTime: 5 * 60_000 });
  const b = useBolsas();
  const persona = usePersona();
  const lista = casos.data ?? [];
  const ids = new Set(lista.map((c) => c.id));
  const proyectos = new Set((pr.data ?? []).filter((p) => p.empresaId === empresa.id).map((p) => p.id));
  const suyas = (horas.data ?? []).filter((r) => (r.casoId && ids.has(r.casoId)) || (r.proyectoId && proyectos.has(r.proyectoId)));
  const abiertos = lista.filter((c) => c.estado === 'NUEVO' || c.estado === 'ABIERTO' || c.estado === 'PENDIENTE');
  const delAnio = lista.filter((c) => Date.parse(c.creado) >= inicioAnio.getTime());
  const bolsa = bolsaDeEmpresa(b.data ?? [], empresa.id);
  const bolsasSuyas = (b.data ?? []).filter((x) => x.empresaId === empresa.id);
  if (!lista.length && !bolsa && !suyas.length) return null;

  const hojas = () => [
    {
      nombre: 'Casos',
      filas: [['Caso', 'Asunto', 'Estado', 'Prioridad', 'Área', 'Asignado', 'Creado', 'Cerrado'], ...lista.map((c) => [c.numero != null ? String(c.numero) : '', c.asunto, ESTADO[c.estado] ?? c.estado, prioridadTexto(c.prioridad), c.grupo, persona(c.asignadoId), new Date(c.creado), c.cerradoEn ? new Date(c.cerradoEn) : null])],
    },
    { nombre: `Horas ${inicioAnio.getFullYear()}`, filas: [['Fecha', 'Persona', 'Qué se hizo', 'Horas', 'Facturable'], ...suyas.map((r) => [new Date(r.fecha), persona(r.miembroId), r.descripcion, h(r.minutos), r.facturable ? 'Sí' : 'No'])] },
    {
      nombre: 'Llamadas',
      filas: [['Fecha', 'Dirección', 'Estado', 'Número', 'Contacto', 'Agente', 'Duración (min)'], ...llamadas.map((l) => [l.inicio ? new Date(l.inicio) : null, l.direccion, l.estado, l.numero, l.nombre, l.agente, Math.round((l.duracion / 60) * 10) / 10])],
    },
    {
      nombre: 'Bolsas',
      filas: [
        ['Bolsa', 'Plan', 'Fecha', 'Movimiento', 'Servicio', 'Atendió', 'Horario', 'Trabajado (min)', 'Minutos'],
        ...bolsasSuyas.flatMap((x) => x.movs.map((m) => [x.cliente, nombrePlan(x.plan), new Date(m.fecha), m.tipo === 'RECARGA' ? 'Carga' : 'Consumo', m.concepto, m.atendidoPor, HORARIOS.find((y) => y[0] === m.horario)?.[1] ?? '', m.minutosReales ?? m.minutos, m.minutos])),
      ],
    },
  ];

  return (
    <section className="box soporte-360" aria-label="Soporte del cliente">
      <div className="box-h">
        <h2 className="lbl">Soporte</h2>
        {exporta ? <BotonExcel archivo={`cliente-${empresa.nombre.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '-')}`} hojas={hojas} /> : null}
      </div>
      <div className="bolsa-franja">
        <div>
          <span>Casos abiertos</span>
          <strong>{casos.isLoading ? '…' : abiertos.length}</strong>
        </div>
        <div>
          <span>Casos en {inicioAnio.getFullYear()}</span>
          <strong>{casos.isLoading ? '…' : delAnio.length}</strong>
        </div>
        <div>
          <span>Horas en {inicioAnio.getFullYear()}</span>
          <strong>{horas.isLoading ? '…' : horasTexto(suyas.reduce((s, r) => s + r.minutos, 0))}</strong>
        </div>
        <div>
          <span>Bolsa</span>
          <strong style={{ fontSize: 14 }}>{bolsa ? <PillSaldo saldo={bolsa.saldo} plan={bolsa.plan} /> : <span className="muted">Sin bolsa</span>}</strong>
        </div>
        <div>
          <span>Último caso</span>
          <strong style={{ fontSize: 14 }}>{lista[0] ? ago(lista[0].creado) : '—'}</strong>
        </div>
      </div>
    </section>
  );
}
